"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const CONTACT_INFO = {
  address: "Vršovice, Praha, Czech Republic",
  phone: "+420 607 774 723",
  email: "pradelna@struz.cz",
  hours: "24/7 night-time delivery and pickup"
};
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-card/50 border border-border rounded-lg animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mb-4">
          <Send className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Poptávka odeslána</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full sm:w-auto">
          Odeslat další poptávku
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Jméno je povinné"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Jméno a příjmení</FormLabel>
              <FormControl>
                <Input placeholder="Jan Novák" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Uveďte své celé jméno.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company_name" rules={{
        required: "Název společnosti je povinný"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Název společnosti</FormLabel>
              <FormControl>
                <Input placeholder="Hotel XYZ" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Název firmy či provozovny.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "E-mail je povinný",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Neplatná e-mailová adresa"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>E-mailová adresa</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jan.novak@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Telefon je povinný"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Telefonní číslo</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+420 123 456 789" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="service_type" rules={{
        required: "Vyberte typ služby"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Typ služby</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Vyberte typ služby" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Hotel">Hotel</SelectItem>
                  <SelectItem value="Apartmán">Apartmán</SelectItem>
                  <SelectItem value="Restaurace">Restaurace</SelectItem>
                  <SelectItem value="Spa">Spa</SelectItem>
                  <SelectItem value="Logistika a doručení">Logistika a doručení</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="weekly_load" rules={{
        min: {
          value: 1,
          message: "Musí být alespoň 1"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Odhadovaný týdenní objem (kusů)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="např. 500" {...field} />
              </FormControl>
              <FormDescription className="text-xs">Přibližný počet kusů týdně.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Další informace / specifikace</FormLabel>
            <FormControl>
              <Textarea placeholder="Popište své požadavky, speciální potřeby apod." className="resize-none" rows={4} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
          {submitError}
        </div>}

      <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Odesílání...
          </> : "Odeslat nezávaznou poptávku"}
      </Button>
    </Form>;
}
export default function GetCustomQuote() {
  return <section id="získejte-individuální-nabídku" className="dark bg-background text-foreground py-16 lg:py-24 relative overflow-hidden">
      
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-background to-background pointer-events-none" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Kontaktujte nás
              </h2>
              <p className="text-muted-foreground text-lg max-w-md">
                Jsme připraveni zajistit pro vás špičkový servis. Ozvěte se nám nebo nás navštivte přímo v naší provozovně.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 rounded-lg bg-card/30 border border-border/50 hover:border-primary/50 transition-colors">
                <div className="bg-primary/10 p-3 rounded-full shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Adresa provozovny</h3>
                  <p className="text-muted-foreground">{CONTACT_INFO.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-lg bg-card/30 border border-border/50 hover:border-primary/50 transition-colors">
                <div className="bg-primary/10 p-3 rounded-full shrink-0">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Telefon</h3>
                  <Link to={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="text-muted-foreground hover:text-primary transition-colors">+420 607 774 723</Link>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-lg bg-card/30 border border-border/50 hover:border-primary/50 transition-colors">
                <div className="bg-primary/10 p-3 rounded-full shrink-0">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">E-mail</h3>
                  <Link to={`mailto:pradelna@struz.cz`} className="text-muted-foreground hover:text-primary transition-colors">pradelna@struz.cz</Link>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 rounded-lg bg-card/30 border border-border/50 hover:border-primary/50 transition-colors">
                <div className="bg-primary/10 p-3 rounded-full shrink-0">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Provozní doba</h3>
                  <p className="text-muted-foreground">dle domluvy</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl shadow-xl p-6 md:p-8 lg:p-10">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Získejte individuální nabídku
              </h2>
              <p className="text-muted-foreground">
                Vyplňte formulář níže a we se vám ozveme s nabídkou na míru pro vaše podnikání.
              </p>
            </div>

            <FormProvider formId="quote_form_home" sectionName="get-quote-section" defaultValues={{
            name: "",
            company_name: "",
            email: "",
            phone: "",
            service_type: "",
            weekly_load: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            company_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            service_type: {
              required: true
            },
            weekly_load: {
              min: 1
            }
          }} submitText="Odeslat nezávaznou poptávku" successMessage="Děkujeme za vaši poptávku! Brzy se vám ozveme s individuální nabídkou." errorMessage="Omlouváme se, došlo k chybě při odesílání. Zkuste to prosím znovu." className="w-full">
              <QuoteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}